import { Component, inject, Input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthorizationService } from '@app/core/auth/authorization.service';
import { IsBlvDirective } from '@app/core/auth/is-blv.directive';
import { DeaktivierungsGrund } from '@app/shared/classes/deaktivierungs-grund';
import { Meldung } from '@app/shared/classes/meldung';
import { MeldungStorno } from '@app/shared/classes/meldung-storno';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

import type { Sprache } from '@app/shared/sprache';

@Component({
  selector: 'meldung-deaktivieren-dialog',
  templateUrl: './meldung-deaktivieren-dialog.component.html',
  imports: [
    FormsModule,
    IsBlvDirective,
    TranslatePipe,
  ]
})
export class MeldungDeaktivierenDialogComponent {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);

  protected readonly label = this.authorizationService.isTierarzt() ? 'meldung.view.deaktivieren.grund.request' : 'meldung.view.deaktivieren.grund.label';

  private id : number|null = null;
  private stornoKommentar : string|null = null;
  private readonly grund = signal<DeaktivierungsGrund | null>(null);

  protected readonly tierarztSprache = signal<string|null>(null);
  protected readonly gruende = signal<DeaktivierungsGrund[]>([]);
  protected readonly sprache = signal<Sprache>('DE');
  protected readonly kommentar = signal<string>('');
  protected readonly error = signal<string|null>(null);
  protected readonly pendingRequest = signal(false);

  readonly result = output<Meldung>();

  @Input()
  set meldung(value: Meldung | null) {
    this.id = value?.id ?? null;
    this.error.set(null);
    if (value && this.authorizationService.isBlv()) {
      this.stornoKommentar = value.stornoKommentar;
      this.backendService.letzteSprache(value.id)
        .subscribe({
          next: sprache => {
            if (sprache) {
              this.setSprache(sprache);
              this.tierarztSprache.set(
                this.translateService.instant('meldung.view.deaktivieren.sprache', {
                  sprache: this.translateService.instant('global.sprache.' + sprache.toLowerCase())
                })
              );
            } else {
              this.tierarztSprache.set(null);
            }
          },
          error: console.error
        });
      this.backendService.listDeaktivierungsGruende()
        .subscribe( gruende => {
          this.gruende.set(gruende);
          if (this.stornoKommentar) {
            this.setGrund(gruende.find( it =>
              it.textDe?.includes('{{ANFRAGE}}') ??
              it.textFr?.includes('{{ANFRAGE}}') ??
              it.textIt?.includes('{{ANFRAGE}}')
            ) ?? null);
          }
        });
    }
  }

  cancel(): void {
    Swal.close();
  }

  save(): void {
    if (!this.id) {
      throw new Error("Meldung not yet selected");
    }
    if (!this.kommentar() || this.kommentar().length < 10) {
      this.error.set(this.translateService.instant('meldung.view.deaktivieren.grund.tooshort'));
      return;
    }
    this.error.set(null);
    this.pendingRequest.set(true);

    const storno: MeldungStorno = { id: this.id, kommentar: this.kommentar() };
    this.backendService.stornoMeldung(storno)
      .subscribe({
        next: meldung => {
          this.pendingRequest.set(false);
          Swal.close();
          this.result.emit(meldung)
        },
        error: err => {
          this.pendingRequest.set(false);
          console.error(err);
        }
      });
  }

  convertGrund(grund: DeaktivierungsGrund | null, sprache: Sprache): string {
    let text: string | undefined;
    switch(sprache) {
      case 'DE': text = grund?.textDe; break;
      case 'FR': text = grund?.textFr; break;
      case 'IT': text = grund?.textIt; break;
    }
    return text?.replace('{{ANFRAGE}}', this.stornoKommentar ?? '') ?? '';
  }

  setSprache(sprache: Sprache): void {
    const oldKommentar = this.convertGrund(this.grund(), this.sprache());
    this.sprache.set(sprache);
    this.kommentar.update(kommentar =>
        kommentar === oldKommentar ? this.convertGrund(this.grund(), sprache) : kommentar
    )
  }

  setGrund(grund: DeaktivierungsGrund | null): void {
    this.grund.set(grund);
    const newKommentar = this.convertGrund(grund, this.sprache());
    this.kommentar.set(newKommentar);
  }
}
